<template>
  <v-container>
    <v-layout row wrap class="mb-4">
      <v-flex xs12>
        <v-layout align-center>
          <h1 class="primary--text mr-3">Data Entries</h1>
          <v-btn small depressed color="accent" to="/aids/create" v-if="$can('create_aid')">
            <v-icon small>add</v-icon>Add Entry
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field v-model="search" append-icon="search" label="Search" single-line></v-text-field>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- List Aids -->
    <v-layout row wrap>
      <v-flex xs12>
        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span v-if="search !== ''">Results for "{{ search }}"</span>
            <span v-else>{{ tableTitle }}</span>
            <v-spacer></v-spacer>
            <export
              v-if="search === ''"
              :data="exportAids"
              :fields="exportFields"
              worksheet="Interventions"
              name="interventions.xls"
              type="csv"
            >
              <v-btn small depressed color="accent" class="ma-0">
                <v-icon small class="mr-2">archive</v-icon>Export
              </v-btn>
            </export>
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="filteredAids"
            :loading="isLoading"
            :search="search"
            :rows-per-page-items="[30, 60, 100]"
            no-data-text="No data entries found"
            :hide-actions="aids.length < 5"
          >
            <template v-slot:items="props">
              <td>{{ (props.index + 1) }}</td>
              <td>{{ formatDate(props.item.timestamp) }}</td>
              <td>{{ formatDate(props.item.timestamp_end) }}</td>
              <td class="py-3">
                <router-link :to="'/aids/' + props.item._id" v-if="canViewAid(props.item.agent_id)">
                  <b>{{ props.item.description }}</b>
                </router-link>
                <b v-else>{{ props.item.description }}</b>
              </td>
              <td class="py-3">
                <router-link :to="'/events/' + props.item.event_id">{{ props.item.event }}</router-link>
              </td>
              <td>{{ props.item.agency }}</td>
              <td>{{ formatCurrency(props.item.value) }}</td>
              <td class="py-3">
                {{ props.item.state }}
                <br />
                <span class="grey--text">{{ props.item.location }}</span>
              </td>

              <td>
                <router-link :to="'/aids/' + props.item._id" v-if="canViewAid(props.item.agent_id)">
                  <v-icon small>visibility</v-icon>
                </router-link>
                <v-icon small class="ml-2 d-none" @click="deleteAid(props.item._id)">delete</v-icon>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { AID_FETCH } from '@/store/actions/aid'
import { STATE_FETCH } from '@/store/actions/state'
import { AGENCY_FETCH } from '@/store/actions/agency'
import { LOCATION_FETCH } from '@/store/actions/location'
import { EVENT_FETCH } from '@/store/actions/event'
import { setFormData } from '@/utils/http'
import rolesData from '@/data/roles'

export default {
  name: 'Aids',
  created () {
    this.isLoading = true
    Promise.all([
      this.$store.dispatch(AID_FETCH),
      this.$store.dispatch(STATE_FETCH),
      this.$store.dispatch(AGENCY_FETCH),
      this.$store.dispatch(LOCATION_FETCH),
      this.$store.dispatch(EVENT_FETCH)
    ]).finally(() => {
      this.isLoading = false
    })
  },
  data () {
    return {
      search: this.$route.query.s || '',
      isLoading: false,
      headers: [
        { text: '#', value: 'index', sortable: false },
        { text: 'Start Date', value: 'timestamp' },
        { text: 'End Date', value: 'timestamp' },
        { text: 'Description', value: 'description' },
        { text: 'Event', value: 'event' },
        { text: 'Agency', value: 'agency' },
        { text: 'Value', value: 'value' },
        { text: 'Location', value: 'state' },
        { text: 'Actions', value: 'location', sortable: false }
      ],
      exportFields: {
        'S/N': 'index',
        'Description': 'description',
        'Event': 'event',
        'Start Date': {
          field: 'timestamp',
          callback: value => this.formatDate(value)
        },
        'End Date': {
          field: 'timestamp_end',
          callback: value => this.formatDate(value)
        },
        'Agency': 'agency',
        'Sponsor': 'sponsor',
        'Value (₦)': {
          field: 'value',
          callback: value => {
            return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(value)
          }
        },
        'State': 'state',
        'LGA': 'lga'
      }
    }
  },
  computed: {
    tableTitle () {
      let title = ''
      if (this.currentUser.role === rolesData.admin) {
        title = 'All Data Entries'
      }
      else if (this.currentUser.role === rolesData.stateHead) {
        title = `All Data Entries in ${this.$store.getters.stateName(this.currentUser.parent_id)}`
      }
      else if (this.currentUser.role === rolesData.agencyHead) {
        title = `Interventions Sponsored by ${this.$store.getters.agencyName(this.currentUser.parent_id)}`
      }
      else {
        title = 'All Data Entries by You'
      }
      return title
    },
    filteredAids () {
      let aids = []
      if (this.currentUser.role === rolesData.admin) {
        aids = this.aids
      }
      else if (rolesData.stateAgents.includes(this.currentUser.role)) {
        aids = this.$store.getters.aidsByField('state_id', this.currentUser.parent_id)
      }
      else if (rolesData.agencyAgents.includes(this.currentUser.role)) {
        aids = this.$store.getters.aidsByField('agency_id', this.currentUser.parent_id)
      }
      else {
        aids = this.$store.getters.aidsByField('agent_id', this.currentUser._id)
      }

      return aids
    },
    exportAids () {
      return this.filteredAids.map((aid, index) => {
        aid.index = index + 1
        return aid
      })
    },
    ...mapGetters(['aids', 'currentUser'])
  },
  methods: {
    canViewAid (agentId) {
      return this.currentUser.role === rolesData.admin || 
        rolesData.heads.includes(this.currentUser.role) || 
        this.currentUser._id === agentId
    },
    formatDate (timestamp) {
			return (new Date(timestamp * 1000)).toLocaleDateString(
				'en-US', { year: 'numeric', month: 'long', day: 'numeric' }
			)
    },
    formatCurrency (value) {
      const currency = new Intl.NumberFormat(
      'en-US', 
        { minimumFractionDigits: 2 }
      ).format(value)

      return '₦' + currency
    },
    deleteAid (id) {
      this.isLoading = true
      const params = setFormData({ _id: id })
      this.$http.post('aid/delete', params)
        .then(() => {
          this.$store.dispatch(AID_FETCH)
            .finally(() => (this.isLoading = false))
        })
    }
  }
}
</script>
